import type { LevelAssessmentDebug } from "@shared/labs/level-assessment-debug";

export interface DebugViewProps {
  debug: LevelAssessmentDebug;
}

/**
 * In-app inspection screen — surfaces every intermediate state in the
 * pipeline so the operator can audit "what did the system see, decide,
 * and output". Off by default; opt-in via `?debug=1` URL param or the
 * "Modo inspeção" checkbox in the form.
 *
 * Each block is a collapsed `<details>` so the page stays scannable —
 * the operator opens just the layer they care about.
 *
 * Sections (in pipeline order):
 *   1. Origem — raw LinkedIn JSON + free text
 *   2. Features V2 — feature vector per dim (debug-only)
 *   3. LLM Candidato — per-axis prompts (cross + vertical) + raw output
 */
export function DebugView({ debug }: DebugViewProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <DebugIntro />
      <DebugSection title="1 · Origem do candidato" subtitle="Perfil cru, texto livre">
        <SourceBlock debug={debug} />
      </DebugSection>

      <DebugSection
        title="2 · Features V2"
        subtitle="Vetor numérico por dimensão — inclui o agrupamento de empregadores em tenure"
      >
        <V2FeaturesBlock v2Grades={debug.v2Grades} />
      </DebugSection>

      <DebugSection
        title="3 · LLM Candidato"
        subtitle="Prompts por eixo concatenados, cross + vertical (separados por '═══ [scope] <eixo> ═══') · resposta = { scores, errors, cross, vertical, overall }"
      >
        {debug.llmCalls.candidate ? (
          <LlmCallBlock call={debug.llmCalls.candidate} />
        ) : (
          <EmptyBlock>(não rodou)</EmptyBlock>
        )}
      </DebugSection>
    </div>
  );
}

function DebugIntro() {
  return (
    <div
      className="card"
      style={{
        padding: "16px 20px",
        background: "var(--r-sand, #F4F1EA)",
        borderColor: "var(--r-line, #e0e0d8)",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--r-ink-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 4,
        }}
      >
        Modo inspeção
      </div>
      <div style={{ fontSize: 13, color: "var(--r-ink-2, #404040)", lineHeight: 1.55 }}>
        Todas as etapas do pipeline, na ordem em que aconteceram. Cada bloco abre sob demanda —
        entrada → features → LLMs → combiner. Útil pra entender por que um candidato saiu com um
        nível ou por que um flag disparou.
      </div>
    </div>
  );
}

function DebugSection({
  title,
  subtitle,
  children,
  defaultOpen = false,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details className="card" open={defaultOpen} style={{ padding: 0 }}>
      <summary
        style={{
          cursor: "pointer",
          listStyle: "none",
          padding: "16px 20px",
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--r-ink)" }}>{title}</div>
          {subtitle && (
            <div
              style={{
                fontSize: 12,
                color: "var(--r-ink-muted)",
                marginTop: 2,
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
        <span className="axis-card-disclosure" style={{ fontSize: 11, fontWeight: 600 }} />
      </summary>
      <div
        style={{
          padding: "0 20px 20px",
          borderTop: "1px solid var(--r-line, #e0e0d8)",
        }}
      >
        <div style={{ paddingTop: 16 }}>{children}</div>
      </div>
    </details>
  );
}

function EmptyBlock({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 13,
        color: "var(--r-ink-muted)",
        fontStyle: "italic",
        padding: "8px 0",
      }}
    >
      {children}
    </div>
  );
}

function SourceBlock({ debug }: { debug: LevelAssessmentDebug }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {debug.freeText && (
        <Subsection label="Texto livre (input do operador)">
          <PreBox>{debug.freeText}</PreBox>
        </Subsection>
      )}
      {debug.rawProfile !== null && (
        <Subsection label="Perfil cru da Apify (JSON)">
          <PreBox>{JSON.stringify(debug.rawProfile, null, 2)}</PreBox>
        </Subsection>
      )}
      {!debug.freeText && debug.rawProfile === null && (
        <EmptyBlock>(sem perfil cru disponível)</EmptyBlock>
      )}
      <Subsection label="Evidência do perfil (parseada da Apify / texto livre)">
        <PreBox>{debug.candidateEvidence}</PreBox>
      </Subsection>
    </div>
  );
}

function V2FeaturesBlock({ v2Grades }: { v2Grades: LevelAssessmentDebug["v2Grades"] }) {
  const dims = Object.keys(v2Grades);
  if (dims.length === 0) {
    return <EmptyBlock>(nenhuma feature V2 calculada — perfil sem dados suficientes)</EmptyBlock>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {dims.map((dim) => {
        const features = v2Grades[dim]?.features ?? {};
        const filled = Object.entries(features).filter(([, v]) => v !== null);
        return (
          <div key={dim}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "var(--r-ink-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginBottom: 6,
              }}
            >
              {dim} · {filled.length} feature(s)
            </div>
            {filled.length === 0 ? (
              <EmptyBlock>(todas as features dessa dim retornaram null)</EmptyBlock>
            ) : (
              <table
                style={{
                  width: "100%",
                  fontSize: 12,
                  borderCollapse: "collapse",
                }}
              >
                <tbody>
                  {filled.map(([k, v]) => (
                    <tr key={k} style={{ borderTop: "1px solid var(--r-line, #e0e0d8)" }}>
                      <td
                        style={{
                          padding: "6px 8px",
                          fontFamily: "'JetBrains Mono', monospace",
                          color: "var(--r-ink-muted)",
                          fontSize: 11,
                          width: "40%",
                          verticalAlign: "top",
                        }}
                      >
                        {k}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "var(--r-ink)",
                          fontWeight: 600,
                          fontVariantNumeric: "tabular-nums",
                          verticalAlign: "top",
                        }}
                      >
                        {formatFeatureValue(v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LlmCallBlock({
  call,
}: {
  call: {
    systemPrompt: string;
    userMessage: string;
    rawOutput: unknown;
  };
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Subsection label="System prompt (enviado ao LLM)">
        <PreBox>{call.systemPrompt}</PreBox>
      </Subsection>
      <Subsection label="User message">
        <PreBox>{call.userMessage}</PreBox>
      </Subsection>
      <Subsection label="Resposta estruturada (validada pelo Zod)">
        <PreBox>{JSON.stringify(call.rawOutput, null, 2)}</PreBox>
      </Subsection>
    </div>
  );
}

function Subsection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "var(--r-ink-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function PreBox({ children }: { children: React.ReactNode }) {
  return (
    <pre
      style={{
        background: "var(--r-sand-step, #ebebe8)",
        padding: "12px 14px",
        borderRadius: 6,
        fontSize: 11,
        lineHeight: 1.55,
        color: "var(--r-ink)",
        fontFamily: "'JetBrains Mono', monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 480,
        overflow: "auto",
        margin: 0,
      }}
    >
      {children}
    </pre>
  );
}

function formatFeatureValue(v: number | boolean | string | null): string {
  if (v === null) return "—";
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") {
    // Show 2 decimals for non-integers, integers as-is.
    return Number.isInteger(v) ? `${v}` : v.toFixed(2);
  }
  return v;
}
